import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Claim } from '../../shared/ui/claim';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { PageBand, PageHeader } from '../../shared/ui/layout';
import { Sparkline } from '../../shared/ui/sparkline';
import { StatusBadge } from '../../shared/ui/status-badge';
import { ApiError } from '../../shared/api/errors';
import { ErrorState, ForbiddenState, LoadingState } from '../../shared/ui/states';
import { clockSeconds, clockTime, relativeTime } from '../../shared/lib/format';
import { IssueActions } from '../../features/issue-actions';
import { FlowIssuePage } from './flow-issue/flow-issue';
import {
  duration,
  issueDetail,
  issueStatement,
  issueTone,
  plural,
  sparkBuckets,
  type TIssueDetail,
} from '../../entities/issue';

const CHART_BARS = 24;

const chartWindow = (issue: TIssueDetail, now: number) => {
  const span = issue.binMs * CHART_BARS;
  const last = new Date(issue.lastSeen).getTime();
  const end = now - last > span ? last + issue.binMs : now;
  return { start: end - span, end, span };
};

interface IIssuePageProps {
  issueId: string;
  timeZone?: string;
  viewer?: { id: string; name: string | null };
}

// A flow step's issue has its own page; the rest are about a source (sender authentication).
export const IssuePage = ({ issueId, timeZone, viewer }: IIssuePageProps) => {
  const issue = useQuery({ queryKey: ['issue', issueId], queryFn: () => issueDetail(issueId) });
  const now = useMemo(() => Date.now(), [issue.dataUpdatedAt]);
  const data = issue.data;

  if (issue.isError) {
    return (
      <div className="p-page">
        {issue.error instanceof ApiError && issue.error.status === 403 ? (
          <ForbiddenState needs="events:read" />
        ) : (
          <ErrorState error={issue.error} onRetry={() => void issue.refetch()} />
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-page">
        <LoadingState rows={6} />
      </div>
    );
  }

  if (data.flowId) {
    return <FlowIssuePage issue={data} flowId={data.flowId} timeZone={timeZone} />;
  }

  const statement = issueStatement(data, now, timeZone);
  const window = chartWindow(data, now);
  const bars = sparkBuckets(data.bins, window.end, window.span, CHART_BARS);
  const counted = data.eventCount;
  const closed = data.state === 'resolved' || data.state === 'ignored';
  const lastSeenMs = new Date(data.lastSeen).getTime();
  const resolvedMs = data.resolvedAt ? new Date(data.resolvedAt).getTime() : null;

  return (
    <>
      <PageHeader
        width="wide"
        title={data.title}
        breadcrumbs={
          <Link to="/issues" className="text-muted hover:text-primary">
            Issues
          </Link>
        }
        description={
          <span className="flex items-center gap-3">
            <StatusBadge vocabulary="issue" value={data.state} />
            <span>{statement.subtitle}</span>
          </span>
        }
        actions={<IssueActions issue={data} />}
      />

      <PageBand width="wide" className="flex flex-col gap-5 py-5">
        <div>
          <Claim
            basis={[
              `${counted.toLocaleString('en-US')} ${plural(counted, 'failure', 'failures')} on ${
                data.sourceName ?? 'this source'
              }`,
              data.basis,
              `the first failure was ${clockSeconds(data.firstSeen, timeZone)}`,
            ]
              .filter((line): line is string => Boolean(line))
              .map((line) => (
                <span key={line} className="flex gap-2">
                  <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-step" />
                  <span className="text-data leading-20 text-muted">{line}</span>
                </span>
              ))}
          >
            {statement.sentence}
          </Claim>

          {data.conclusion && (
            <>
              <p className="mt-3 font-mono text-micro text-faint">What we think it is</p>
              <p className="mt-1 text-data leading-20 text-muted">{data.conclusion}</p>
            </>
          )}
        </div>

        <div>
          <p className="mb-inset font-mono text-micro text-faint">
            Failures per {duration(data.binMs)}
          </p>
          <Sparkline
            values={bars}
            tone={issueTone(data.state, now - lastSeenMs, data.binMs)}
            height="chart"
            gap={3}
            label={`${counted.toLocaleString('en-US')} ${plural(
              counted,
              'failure',
              'failures',
            )}, ${CHART_BARS} buckets of ${duration(data.binMs)}`}
          />
          <div className="relative mt-2 h-6 font-mono text-micro text-faint">
            <span className="absolute bottom-0 left-0">
              {clockTime(new Date(window.start).toISOString(), timeZone)}
            </span>
            <span className="absolute right-0 bottom-0">
              {clockTime(new Date(window.end).toISOString(), timeZone)}
            </span>
          </div>
        </div>

        <FactList>
          {data.acknowledgedAt && (
            <FactRow label="Taken by">
              {`${
                data.acknowledgedByName ??
                (data.acknowledgedBy === viewer?.id ? (viewer.name ?? 'you') : 'someone')
              }, ${relativeTime(data.acknowledgedAt, now, timeZone)}`}
            </FactRow>
          )}
          <FactRow label="Last failure">
            {`${clockSeconds(data.lastSeen, timeZone)}, ${relativeTime(data.lastSeen, now, timeZone)}`}
          </FactRow>
          {!closed && statement.quietFor && (
            <FactRow label="Quiet for">{statement.quietFor}</FactRow>
          )}
          {data.resolvedAt && resolvedMs !== null && (
            <FactRow label={data.state === 'ignored' ? 'Ignored' : 'Closed'}>
              {`${clockTime(data.resolvedAt, timeZone)}, ${relativeTime(
                data.resolvedAt,
                now,
                timeZone,
              )} — ${duration(Math.max(0, resolvedMs - lastSeenMs))} after that, ${
                data.resolution === 'auto'
                  ? 'because it stopped recurring'
                  : data.resolution === 'ignored'
                    ? 'by being ignored'
                    : 'by a person'
              }`}
            </FactRow>
          )}
          {data.resolutionNote && (
            <FactRow
              label={data.resolution === 'auto' ? 'Hookpipe wrote' : 'Whoever closed it wrote'}
            >
              {data.resolutionNote}
            </FactRow>
          )}
          {!closed && (
            <FactRow label="Closes itself when quiet" tone={data.autoResolve ? 'default' : 'muted'}>
              {data.autoResolve ? 'yes' : 'no — a person has to'}
            </FactRow>
          )}
          {data.alertSuppressed && (
            <FactRow label="Alerting" tone="muted">
              silenced by a rule — listed and counted, but nobody was told
            </FactRow>
          )}
        </FactList>
      </PageBand>
    </>
  );
};
